import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Trash2, Search, X, Eye, EyeOff, RefreshCw, Settings, Users, Edit } from 'lucide-react';
import TenantUsersPanel from './TenantUsersPanel';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
function apiWithToken(token) { const i = axios.create({ baseURL: BACKEND }); i.interceptors.request.use(c => { c.headers.Authorization = 'Bearer ' + token; return c; }); return i; }

const MODULE_LABELS = {
  requisitions:'Purchase Requisitions', orders:'Purchase Orders', rfq:'RFx Sourcing',
  contracts:'Contract Management', goods_receiving:'Receiving', invoicing:'Invoicing', assets:'Asset Management',
  inventory:'Inventory Control', expenses:'Employee Expenses', supplier_portal:'Supplier Portal', sales_orders:'Sales Order Processing',
  sites:'Multi-Site / Multi-Company', ocr:'Invoice OCR', recipe:'Recipe Management',
};
const ALL_MODULE_KEYS = Object.keys(MODULE_LABELS);

export default function TenantEditModal({ tenant, token, onClose, onSaved }) {
  const { t } = useTranslation();
  const api = apiWithToken(token);
  const [modules, setModules] = useState(tenant?.modules || []);
  const [licenseLimit, setLicenseLimit] = useState(tenant?.user_license_limit || 10);
  const [status, setStatus] = useState(tenant?.status || 'active');
  const [notes, setNotes] = useState(tenant?.notes || '');
  // Editable client info
  const [companyName,    setCompanyName]    = useState(tenant?.company_name    || '');
  const [companyAddress, setCompanyAddress] = useState(tenant?.company_address || '');
  const [companyCity,    setCompanyCity]    = useState(tenant?.company_city    || '');
  const [companyCountry, setCompanyCountry] = useState(tenant?.company_country || '');
  const [phone,          setPhone]          = useState(tenant?.phone           || '');
  const [billingEmail,   setBillingEmail]   = useState(tenant?.billing_email   || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/eyvo-admin/tenants/${tenant.tenant_id}`, {
        company_name: companyName, company_address: companyAddress,
        company_city: companyCity, company_country: companyCountry,
        phone, billing_email: billingEmail, status, notes,
      });
      await api.put(`/api/eyvo-admin/tenants/${tenant.tenant_id}/modules`, { modules });
      await api.put(`/api/eyvo-admin/tenants/${tenant.tenant_id}/licenses`, { user_license_limit: licenseLimit });
      toast.success('Tenant updated');
      onSaved(); onClose();
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Save failed'); }
    setSaving(false);
  };

  const toggleModule = (id) =>
    setModules(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Tenant — {tenant.company_name}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger value="details">Client Details</TabsTrigger>
            <TabsTrigger value="modules">{t('term.modulesLicences')}</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
          </TabsList>

          {/* ── Client Details tab ── */}
          <TabsContent value="details" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>Client Name *</Label>
                <Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Company trading name" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Address</Label>
                <Input value={companyAddress} onChange={e => setCompanyAddress(e.target.value)} placeholder="Street address" />
              </div>
              <div className="space-y-1.5">
                <Label>City</Label>
                <Input value={companyCity} onChange={e => setCompanyCity(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Country</Label>
                <Input value={companyCountry} onChange={e => setCompanyCountry(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Phone Number</Label>
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 555 000 0000" />
              </div>
              <div className="space-y-1.5">
                <Label>Billing Email</Label>
                <Input type="email" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <select value={status} onChange={e => setStatus(e.target.value)}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Licensed Users</Label>
                <div className="flex items-center gap-3">
                  <Input type="number" value={licenseLimit} onChange={e => setLicenseLimit(parseInt(e.target.value)||1)} className="w-32" min={1} data-testid="license-limit-input" />
                  <span className="text-xs text-muted-foreground">Current usage: {tenant?.user_count || 0} / {licenseLimit}</span>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Internal Notes (Eyvo staff only)</Label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none" />
            </div>
          </TabsContent>

          {/* ── Modules & Licences tab ── */}
          <TabsContent value="modules" className="pt-4">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t('term.userLicenceLimit')}</Label>
                <Input type="number" value={licenseLimit} onChange={e => setLicenseLimit(parseInt(e.target.value)||1)} className="w-32" min={1} />
              </div>
              <div>
                <Label className="mb-3 block">Enabled Modules ({modules.length} / {ALL_MODULE_KEYS.length})</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_MODULE_KEYS.map(id => (
                    <label key={id} className={`flex items-center gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                      modules.includes(id) ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
                    }`}>
                      <Switch checked={modules.includes(id)} onCheckedChange={() => toggleModule(id)} />
                      <span className="text-sm font-medium">{MODULE_LABELS[id]}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ── Users tab ── */}
          <TabsContent value="users" className="pt-4">
            <TenantUsersPanel tenantId={tenant.tenant_id} token={token} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
